/**
 * Economy formulas (docs/00 + 01). Pure integer helpers over RunState/config.
 */
import type { BalanceConfig } from '../config/balance'
import type { RunState } from './types'

export function baseIncome(round: number, cfg: BalanceConfig): number {
  return cfg.economy.baseIncomeStart + Math.floor((round - 1) / cfg.economy.baseIncomeDivisor)
}

export function interest(gold: number, cfg: BalanceConfig): number {
  return Math.min(cfg.economy.interestCap, Math.floor(gold / cfg.economy.interestPer))
}

export function isLowHp(state: RunState): boolean {
  return state.hp < state.config.economy.lowHpThreshold
}

export interface IncomeBreakdown {
  winBonus: number
  interest: number
  base: number
  pveLoot: number
  total: number
}

/**
 * Round income, applied when entering a new round's shop phase.
 * Order (docs/00): Win-Bonus zaehlt VOR Zinsberechnung; Low HP Mode (docs/01)
 * deaktiviert Zinsen + Win-Bonus.
 */
export function applyRoundIncome(state: RunState, opts: { cleanWinLastRound: boolean; pveLoot: boolean }): IncomeBreakdown {
  const cfg = state.config
  const lowHp = isLowHp(state)
  const winBonus = !lowHp && opts.cleanWinLastRound ? cfg.economy.winBonus : 0
  state.gold += winBonus
  const zins = lowHp ? 0 : interest(state.gold, cfg)
  const base = baseIncome(state.round, cfg)
  const pveLoot = opts.pveLoot ? cfg.economy.pveLootGold : 0
  state.gold += zins + base + pveLoot
  return { winBonus, interest: zins, base, pveLoot, total: winBonus + zins + base + pveLoot }
}

/** StageBase = const + floor((Round-1)/divisor) (docs/00) */
export function stageBase(round: number, cfg: BalanceConfig): number {
  return cfg.hp.stageBaseConst + Math.floor((round - 1) / cfg.hp.stageBaseDivisor)
}

/** OutcomeMod = ceil(RemainingEnemyPower% x StageBase); power in Basispunkten (0..10000) */
export function outcomeMod(enemyPowerBp: number, sb: number): number {
  return Math.ceil((enemyPowerBp * sb) / 10000)
}

/** Sell = max(ceil(Buy/2), Buy - floor(RoundsHeld/decay)) — Waffen wie Champions (docs/02) */
export function sellValue(buyPrice: number, boughtRound: number, currentRound: number, cfg: BalanceConfig): number {
  const held = Math.max(0, currentRound - boughtRound)
  const floorVal = Math.ceil((buyPrice * cfg.sell.minPercent) / 100)
  return Math.max(floorVal, buyPrice - Math.floor(held / cfg.sell.decayPerRounds))
}

/** Reroll-Kosten: Sequenz [1,2,3,5,7,9], danach +step; +floor((Round-1)/10) (docs/02) */
export function rerollCost(paidRerollsUsed: number, round: number, cfg: BalanceConfig): number {
  const seq = cfg.shop.rerollSequence
  const base = paidRerollsUsed < seq.length
    ? seq[paidRerollsUsed]!
    : seq[seq.length - 1]! + cfg.shop.rerollStep * (paidRerollsUsed - seq.length + 1)
  return base + Math.floor((round - 1) / cfg.shop.rerollRoundDivisor)
}

/** Fracture-Reparatur: base + floor(Round/divisor) (docs/01) */
export function repairCost(round: number, cfg: BalanceConfig): number {
  return cfg.fracture.repairBase + Math.floor(round / cfg.fracture.repairDivisor)
}

/** Deployment-Cap-Kurve (docs/02): R1..10 aus Tabelle, ab R11 +1 alle 2 Runden, Hard Cap */
export function deployCap(round: number, cfg: BalanceConfig): number {
  const curve = cfg.deploy.capCurve
  if (round <= curve.length) return curve[round - 1]!
  const grown = cfg.deploy.capFrom11Base + Math.floor((round - (curve.length + 1)) / 2)
  return Math.min(cfg.deploy.capCeiling, grown)
}

/** Item-Storage-Groesse: meta-skalierend, A7: meta fix 1 -> itemStart */
export function storageSize(cfg: BalanceConfig): number {
  return Math.min(cfg.storage.itemCap, cfg.storage.itemStart + cfg.storage.itemPerMeta * (cfg.meta.level - 1))
}
